import { useEffect, useRef } from 'react';
import io from 'socket.io-client';
import { useQueryClient } from 'react-query';
import { getWebSocketsBaseURL } from '@ubeya/shared/utils/http';
import { HELP_DESK_MESSAGES_QUERY_KEY } from '@ubeya/shared/hooks/account/useHelpDeskMessages';
import { HELP_DESK_TICKETS_QUERY_KEY } from '@ubeya/shared/hooks/account/useHelpDeskTickets';
import { HELP_DESK_TICKET_QUERY_KEY } from '@ubeya/shared/hooks/account/useHelpDeskTicket';
import { HELP_DESK_UNREAD_COUNT_QUERY_KEY } from '@ubeya/shared/hooks/account/useHelpDeskUnreadCount';
import { HELP_DESK_EMPLOYEE_TICKETS_QUERY_KEY } from '@ubeya/shared/hooks/account/useHelpDeskEmployeeTickets';

const HELP_DESK_NAMESPACE = '/help-desk';

/**
 * Connects to the help-desk WebSocket namespace and invalidates
 * react-query caches when messages or tickets are updated in real-time.
 *
 * Joins the room for the given `ticketId` so the server emits scoped events.
 *
 * @param {{ accountId: string|number, ticketId: string|number, enabled?: boolean }} options
 */
const useHelpDeskSocket = ({ accountId, ticketId, enabled = true }) => {
  const queryClient = useQueryClient();
  const socketRef = useRef(null);

  useEffect(() => {
    if (!enabled || !accountId) return;

    const baseURL = getWebSocketsBaseURL();
    const socket = io(`${baseURL}${HELP_DESK_NAMESPACE}`, {
      ...(ticketId ? { query: { ticketId: String(ticketId) } } : {}),
      transports: ['websocket', 'polling'],
      withCredentials: true
    });

    socketRef.current = socket;

    socket.on('message:new', () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === HELP_DESK_MESSAGES_QUERY_KEY &&
          String(query.queryKey[2]) === String(ticketId)
      });
      queryClient.invalidateQueries({
        predicate: (query) => Array.isArray(query.queryKey) && query.queryKey[0] === HELP_DESK_TICKETS_QUERY_KEY
      });
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.length && query.queryKey[0] === HELP_DESK_UNREAD_COUNT_QUERY_KEY
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) && query.queryKey[0] === HELP_DESK_EMPLOYEE_TICKETS_QUERY_KEY
      });
    });

    socket.on('ticket:updated', () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === HELP_DESK_TICKET_QUERY_KEY &&
          String(query.queryKey[2]) === String(ticketId)
      });
      queryClient.invalidateQueries({
        predicate: (query) => Array.isArray(query.queryKey) && query.queryKey[0] === HELP_DESK_TICKETS_QUERY_KEY
      });
      queryClient.invalidateQueries({
        predicate: (query) => Array.isArray(query.queryKey) && query.queryKey[0] === HELP_DESK_UNREAD_COUNT_QUERY_KEY
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) && query.queryKey[0] === HELP_DESK_EMPLOYEE_TICKETS_QUERY_KEY
      });
    });

    socket.on('help-desk:refresh', () => {
      queryClient.invalidateQueries({
        predicate: (query) => Array.isArray(query.queryKey) && query.queryKey[0] === HELP_DESK_TICKETS_QUERY_KEY
      });
      queryClient.invalidateQueries({
        predicate: (query) => Array.isArray(query.queryKey) && query.queryKey[0] === HELP_DESK_UNREAD_COUNT_QUERY_KEY
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) && query.queryKey[0] === HELP_DESK_EMPLOYEE_TICKETS_QUERY_KEY
      });
      if (ticketId) {
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === HELP_DESK_TICKET_QUERY_KEY &&
            String(query.queryKey[2]) === String(ticketId)
        });
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === HELP_DESK_MESSAGES_QUERY_KEY &&
            String(query.queryKey[2]) === String(ticketId)
        });
      }
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [accountId, ticketId, enabled, queryClient]);
};

export default useHelpDeskSocket;
